import { Injectable } from '@angular/core';
import { parse } from 'mathjs/lib/esm/number';
import { BehaviorSubject, Observable, catchError, map, of, shareReplay } from 'rxjs';
import { ProductRepository } from '../repositories/products.repository';

@Injectable({
  providedIn: 'root',
})
export class UnitConversionService {
  constructor(private productRepository: ProductRepository) {}

  // Internal subject for the selected max flow unit. Prefixed with `_` to avoid
  // naming collisions and to make this implementation distinct from other copies.
  private _selectedMaxFlowUnitSubject = new BehaviorSubject<string>('m³/s');

  // Expose the observable as a readonly stream.
  readonly selectedMaxFlowUnit$: Observable<string> = this._selectedMaxFlowUnitSubject.asObservable();

  // Update the selected unit.
  setSelectedUnit(unit: string): void {
    this._selectedMaxFlowUnitSubject.next(unit);
  }

  fetchFormula(valueType: string, sourceUnit: string, targetUnit: string): Observable<string> {
    // Use a cache to avoid hitting the backend multiple times for the
    // same conversion formula. Also share in-flight requests so parallel
    // callers receive the same response instead of issuing duplicate HTTP calls.
    const key = `${valueType}|${sourceUnit}|${targetUnit}`;
    // lazy-init the cache map
    if (!this._formulaCache) {
      this._formulaCache = new Map<string, Observable<string>>();
    }

    const cached = this._formulaCache.get(key);
    if (cached) {
      return cached;
    }

    const obs = this.productRepository.fetchFormula(valueType, sourceUnit, targetUnit).pipe(
      // share the single subscription result with all subscribers
      shareReplay(1),
      // if an error occurs, remove from cache so retries are possible
      catchError((err) => {
        if (this._formulaCache) {
          this._formulaCache.delete(key);
        }
        throw err;
      }),
    );

    this._formulaCache.set(key, obs);
    return obs;
  }

  convert(value: number | null, fromUnit: string, toUnit: string, valueType: string): Observable<number | null> {
    // Handle null or undefined values
    if (value === null || value === undefined) {
      return of(null);
    }

    if (fromUnit === toUnit) {
      // If the source unit and target unit are the same, return the original value
      return of(value);
    }
    // Use our cached fetchFormula so identical requests won't trigger
    // repeated backend calls (this also shares in-flight requests).
    return this.fetchFormula(valueType, fromUnit, toUnit).pipe(map((formula) => this.evaluateAndFormat(formula, value)));
  }

  // Extracted helper: parse, evaluate and format the numeric result from a formula.
  // Keeping this logic private reduces cognitive complexity in `convert` while
  // preserving the original validation and error normalization behaviour.
  private evaluateAndFormat(formula: string, value: number): number {
    try {
      const node = parse(formula);
      const code = node.compile();
      const evalResult = code.evaluate({ x: value });

      if (evalResult === undefined) {
        throw new Error(`formula(${value}) returned undefined`);
      }

      if (evalResult === null) {
        throw new Error(`formula(${value}) returned null`);
      }

      if (Number.isNaN(evalResult)) {
        throw new Error(`formula(${value}) returned NaN`);
      }

      const result = Number(evalResult);
      if (Number.isNaN(result)) {
        throw new Error(`formula(${value}) produced non-numeric result: ${evalResult}`);
      }

      if (Math.abs(result - Math.round(result)) < 0.00001) {
        return Math.round(result);
      }

      // Otherwise, round to two decimal places
      return parseFloat(result.toFixed(2));
    } catch (err) {
      const msg = (err as Error).message || '';
      if (/Undefined symbol/i.test(msg) || /Undefined function/i.test(msg) || /Unknown symbol/i.test(msg)) {
        throw new Error(`Error evaluating conversion formula: ${msg} (is not defined)`);
      }
      throw new Error(`Error evaluating conversion formula: ${msg}`);
    }
  }
  convertMinMax(value: number, fromUnit: string, toUnit: string, valueType: string): Observable<number | null> {
    // Delegate to convert; kept as a separate method for semantic clarity.
    return this.convert(value, fromUnit, toUnit, valueType);
  }

  // internal cache for formula observables
  private _formulaCache?: Map<string, Observable<string>>;
}
